/**
 * The agency website's design tokens, and the one that has to be calculated.
 *
 * An agency picks two colours. Everything else on their site is derived from
 * those, and one derivation is not cosmetic: an accent chosen for a logo is
 * almost never legible as small text on a pale background, and on a
 * white-label product nobody is going to check. Windlebrook's brass accent
 * measures 2.5:1 on their own paper. WCAG 2.2 AA asks for 4.5:1 on body
 * text, and the design's answer is a second, darker token used wherever the
 * accent would otherwise be read rather than looked at.
 *
 * So `accentInk` computes that darker shade rather than asking for it: the
 * brand stays the brand, the eyebrow rules and the hairlines keep the colour
 * the agency chose, and the words on top of the paper are always readable.
 *
 * Colours are HSL triples in Tailwind's space-separated form ("36 36% 52%")
 * because that is what index.css and brand.jsx already speak. Nothing here
 * introduces a second colour format.
 */

// --- colour maths ----------------------------------------------------------

export function parseHsl(value) {
  const m = String(value ?? "").trim()
    .match(/^(-?[\d.]+)\s+(-?[\d.]+)%\s+(-?[\d.]+)%$/);
  if (!m) return null;
  return { h: Number(m[1]), s: Number(m[2]), l: Number(m[3]) };
}

export function formatHsl({ h, s, l }) {
  const round = (n) => Math.round(n * 10) / 10;
  return `${round(h)} ${round(s)}% ${round(l)}%`;
}

function hslToRgb({ h, s, l }) {
  const sN = s / 100;
  const lN = l / 100;
  const c = (1 - Math.abs(2 * lN - 1)) * sN;
  const hp = (((h % 360) + 360) % 360) / 60;
  const x = c * (1 - Math.abs((hp % 2) - 1));
  const [r1, g1, b1] = hp < 1 ? [c, x, 0]
    : hp < 2 ? [x, c, 0]
    : hp < 3 ? [0, c, x]
    : hp < 4 ? [0, x, c]
    : hp < 5 ? [x, 0, c]
    : [c, 0, x];
  const m = lN - c / 2;
  return [r1 + m, g1 + m, b1 + m];
}

/** WCAG relative luminance, from sRGB in 0..1. */
function luminance(rgb) {
  const lin = rgb.map((v) => (v <= 0.03928 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4));
  return 0.2126 * lin[0] + 0.7152 * lin[1] + 0.0722 * lin[2];
}

/** WCAG contrast ratio between two HSL triples. 1 to 21. */
export function contrastRatio(a, b) {
  const ca = typeof a === "string" ? parseHsl(a) : a;
  const cb = typeof b === "string" ? parseHsl(b) : b;
  if (!ca || !cb) return 0;
  const la = luminance(hslToRgb(ca));
  const lb = luminance(hslToRgb(cb));
  const [hi, lo] = la > lb ? [la, lb] : [lb, la];
  return (hi + 0.05) / (lo + 0.05);
}

/** WCAG 2.2 AA for body text. Large text and non-text may sit at 3:1. */
export const AA_BODY = 4.5;
export const AA_LARGE = 3;

/**
 * The accent, moved until it is readable on the paper it sits on.
 *
 * Hue and saturation are left alone: an agency's brass must stay brass, and
 * desaturating it to pass would hand them a different brand. Only lightness
 * moves, and only in the direction that gets there — dark text on pale
 * paper, pale text on a dark site.
 *
 * Returns the accent unchanged when it already passes, so an agency whose
 * colour is readable keeps exactly the value they chose.
 */
export function accentInk(accent, paper, target = AA_BODY) {
  const a = typeof accent === "string" ? parseHsl(accent) : accent;
  const p = typeof paper === "string" ? parseHsl(paper) : paper;
  if (!a || !p) return typeof accent === "string" ? accent : formatHsl(a || { h: 0, s: 0, l: 0 });
  if (contrastRatio(a, p) >= target) return formatHsl(a);

  const paperIsLight = luminance(hslToRgb(p)) > 0.18;
  const step = paperIsLight ? -1 : 1;
  let best = { ...a };
  for (let n = 0; n < 100; n += 1) {
    const l = best.l + step;
    if (l < 0 || l > 100) break;
    best = { ...best, l };
    if (contrastRatio(best, p) >= target) return formatHsl(best);
  }
  // Ran out of room, which means the paper itself is mid-grey. Black or
  // white, whichever wins, rather than a colour that fails quietly.
  return paperIsLight ? "0 0% 0%" : "0 0% 100%";
}

// --- presets ---------------------------------------------------------------

/**
 * Three ways the same content can look. Only type and corner radius change:
 * the palette is always the agency's, so a preset can never make two
 * agencies look like each other.
 */
export const PRESETS = {
  estate: {
    name: "Estate",
    note: "Serif headlines, square corners. The default.",
    fontDisplay: "'Cormorant Garamond', 'Times New Roman', Georgia, serif",
    fontBody: "'Manrope', -apple-system, 'Segoe UI', Helvetica, Arial, sans-serif",
    radius: "2px",
  },
  modern: {
    name: "Modern",
    note: "One sans face throughout, no rounding at all.",
    fontDisplay: "'Manrope', -apple-system, 'Segoe UI', Helvetica, Arial, sans-serif",
    fontBody: "'Manrope', -apple-system, 'Segoe UI', Helvetica, Arial, sans-serif",
    radius: "0px",
  },
  classic: {
    name: "Classic",
    note: "Serif throughout, softer corners.",
    fontDisplay: "'Cormorant Garamond', 'Times New Roman', Georgia, serif",
    fontBody: "'Lora', Georgia, 'Times New Roman', serif",
    radius: "6px",
  },
};

export const DEFAULT_PRESET = "estate";

export function presetFor(name) {
  return PRESETS[String(name || "").toLowerCase()] || PRESETS[DEFAULT_PRESET];
}

/**
 * Every token an agency site paints with, from the agency's own record.
 *
 * The keys are the ones index.css and tailwind.config.js already use, so
 * this adds one derived token (`accent-ink`) and the type and radius, rather
 * than a parallel vocabulary that would have to be kept in step with the
 * first one.
 */
export function siteTokens(brand, presetName) {
  const colors = (brand && brand.colors) || {};
  const preset = presetFor(presetName || (brand && brand.theme_preset));
  const paper = colors.background || "40 15% 97%";
  const accent = colors.accent || "36 36% 52%";
  return {
    ...colors,
    // The accent as words, which is a different colour from the accent as a
    // rule or a background. Named for what it is used for.
    "accent-ink": accentInk(accent, paper),
    "font-display": preset.fontDisplay,
    "font-body": preset.fontBody,
    radius: preset.radius,
  };
}

/**
 * What fails, and on what, so the manager can say so rather than an agency
 * finding out from an accessibility complaint. Text pairs only: a hairline
 * or a background swatch is not read.
 */
export function contrastReport(tokens) {
  const paper = tokens.background || "40 15% 97%";
  const pairs = [
    ["Body text", tokens.foreground || tokens.primary, paper, AA_BODY],
    ["Headings", tokens.primary, paper, AA_BODY],
    ["Accent as text", tokens["accent-ink"], paper, AA_BODY],
    ["Muted text", tokens["muted-foreground"], paper, AA_BODY],
  ];
  return pairs
    .filter(([, fg]) => Boolean(fg))
    .map(([label, fg, bg, target]) => {
      const ratio = contrastRatio(fg, bg);
      return { label, ratio: Math.round(ratio * 100) / 100, target, passes: ratio >= target };
    });
}

/**
 * An agency's chosen colours plus the ones derived from them.
 *
 * Called on every palette repaint rather than stored on the record, so an
 * agency who changes their accent in the manager cannot end up with a
 * legible shade of the colour they used to have.
 *
 * Anything already set on the record wins: an agency that has explicitly
 * chosen an `accent-ink` keeps it.
 */
export function withDerivedTokens(colors) {
  const out = { ...(colors || {}) };
  if (!out["accent-ink"] && out.accent) {
    out["accent-ink"] = accentInk(out.accent, out.background || "40 15% 97%");
  }
  return out;
}
